import React, { ReactElement } from 'react';
import { Button, Link } from 'idsk-react-components';
import './pracovneKroky.scss';
import PracovnyKrokRequest from '../../../../../model/pracovnyKrok/PracovnyKrokRequest';
import PracovnyKrokEnum from '../../../../../model/pracovnyKrok/PracovnyKrokEnum';
import PracovnyKrokComplete from './component/PracovnyKrokComplete';
import externalRoutes from '../../../../../routing/externalRoutes';
import routes from '../../../../../routing/routes';
import OdoslatBezPripomienok from './component/OdoslatBezPripomienok';
import UkoncitProces from './component/UkoncitProces';
import OdoslatNaUvsr from './component/OdoslatNaUvsr';
import PracovnyKrokListRequest from '../../../../../model/pracovnyKrok/PracovnyKrokListRequest';
import LegislativnyMaterialItem from '../../../../../model/pracovnyKrok/LegislativnyMaterialItem';
import splitCisloLegMaterialu from '../../../../../function/splitCisloLegMaterialu';
import UkoncitVyhodnotenieMpk from './component/UkoncitVyhodnotenieMpk';
import PredbezneStanovisko from './component/PredbezneStanovisko';
import StadiumMaterialuHistoria
  from '../../../../../model/legislativnyMaterial/StadiumMaterialuHistoria';

interface Props {
  legMaterial: LegislativnyMaterialItem;
  reloadDetail?: () => void;
  pracovneKroky: PracovnyKrokListRequest;
  reloadPracovneKroky: () => void;
  ulohy?: boolean;
  setIsDropdownOpened: (isDropdownOpened: boolean) => void;
  historiaRecords?: Array<StadiumMaterialuHistoria>;
}

const PracovneKroky = ({
  legMaterial,
  reloadDetail,
  pracovneKroky,
  reloadPracovneKroky,
  ulohy = false,
  setIsDropdownOpened,
  historiaRecords,
}: Props): ReactElement => {
  const {
    type,
    year,
    index,
  } = splitCisloLegMaterialu(legMaterial.cisloLegislativnehoMaterialu);

  const notifyParent = (link: string): void => {
    window.parent.postMessage({
      type: 'iframe-to-parent-external-route',
      route: link,
    }, '*');
  };

  const getMessage = (nazov: string): string => `Naozaj si prajete vykonať pracovný krok "${nazov}"?`;

  const getPracovnyKrok = (pracovnyKrok: PracovnyKrokRequest): ReactElement => {
    switch (pracovnyKrok.pracovnyKrok) {
      case PracovnyKrokEnum.VYTVORIT_SPRIEVODNY_DOKUMENT:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index)}
            type="link"
          > {pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.NAVRAT_PRIPRAVA:
      case PracovnyKrokEnum.PRECHOD_PREDP_FAZA_UKONC:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion={getMessage(pracovnyKrok.nazov)}
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa ukončí aktuálne štádium a odstránia sa z '
              + 'Vášho zásobníka práce pracovné kroky, ktoré patria do aktuálneho štádia tohto materiálu a spustí sa '
              + 'nasledujúce štádium.'}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            legMaterial={legMaterial}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.UKONCIT_MPK_TEST:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ZRUSIT_NASTAVENE_VYJADRENIA:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion='Naozaj si prajete zrušiť nastavené štádium "Vyjadrenia k predbežnej informácii"?'
            confirmActionDescription={'Po vykonaní tohto pracovného kroku bude zrušená konfigurácia štádia '
              + '"Vyjadrenia k predbežnej informácii", ktorého začiatok bol nastavený na neskorší dátum a budú '
              + 'obnovené štandardné pracovné kroky štádia "Príprava materiálu".'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            className={
              `pracovny-krok__button--danger ${pracovneKroky.pracovneKroky.length > 1
                ? 'pracovny-krok__divider' : ''}`
            }
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.SCHVALIT_GESTORA:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete schváliť gestora?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa spustí štádium "Zápis predbežného '
              + 'stanoviska k návrhu aktu EÚ" a nebude možné vykonávať pracovné kroky dostupné v aktuálnom štádiu.'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.SCHVALIT_GESTORA_PRESUN:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete presunúť proces do ďalšieho štádia?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa spustí štádium "Zápis predbežného '
              + 'stanoviska k návrhu aktu EÚ" a nebude možné vykonávať pracovné kroky dostupné v aktuálnom štádiu.'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.PRECHOD_SCHVALOVANIE_MINISTROM:
      case PracovnyKrokEnum.PRECHOD_VYTVORENIE_FINAL_STANOVISKA:
      case PracovnyKrokEnum.PRECHOD_SPRACOVANIE_PREDKLADATEL:
      case PracovnyKrokEnum.PRECHOD_PRED_VYHLASENIM:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion={getMessage(pracovnyKrok.nazov)}
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa ukončí aktuálne  štádium a odstránia sa z '
              + 'Vášho zásobníka práce pracovné kroky, ktoré patria do aktuálneho štádia tohto materiálu a spustí sa '
              + 'nové štádium. '}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.PRECHOD_ZAPIS_DOPLNENEHO_PREDB_STANOVISKA:
        return (
          <PredbezneStanovisko
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
            historiaRecords={historiaRecords}
          />
        );
      case PracovnyKrokEnum.OZNACIT_AKO_SCHVALENY_MINISTER:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete označiť materiál ako schválený?"
            legMaterial={legMaterial}
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa spustí štádium '
              + '"Pred vyhlásením v Zbierke zákonov SR".'}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ODOSLAT_PREDSEDOVI_VLADY:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.podpisPredseda.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          > {pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.OZNACIT_AKO_NESCHVALENY_MINISTER:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete označiť materiál ako neschválený?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku bude proces ukončený '
              + 'a nebude možné vykonávať ďalšie pracovné kroky. Aktuálne štádium materiálu '
              + 'sa zmení na na "Proces ukončený".'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.NAVRAT_SPRACOVANIE_PREDKLADATEL:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion={'Naozaj si prajete vrátiť materiál do štádia "Spracovanie predkladateľom"?'}
            confirmActionDescription={'Po vykonaní tohto pracovného kroku už nebude možné vykonávať pracovné kroky v '
              + 'aktuálnom štádiu.'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.UKONCIT_ROKOVANIE_VLADY:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion='Naozaj si prajete ukončiť štádium "Rokovanie vlády SR"?'
            confirmActionDescription={'Po ukončení štádia "Rokovanie vlády SR" už nebude možné toto '
              + 'štádium spustiť opakovane.'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            redirect={false}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.UKONCIT_ROKOVANIE_POV:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion='Naozaj si prajete ukončiť štádium "Rokovanie poradných orgánov vlády"?'
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.SPUSTIT_ROKOVANIE_VLADY:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion='Naozaj si prajete spustiť štádium "Rokovanie vlády SR"?'
            confirmActionDescription={'Po spustení štádia "Rokovanie vlády SR" už v tomto štádiu '
              + 'nebude možné spustiť štádium "Rokovanie poradných orgánov vlády". '}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ZRUSIT_NASTAVENE_MPK:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion='Naozaj si prajete zrušiť nastavené štádium "Pripomienkové konanie"?'
            confirmActionDescription={`Po vykonaní tohto pracovného kroku bude zrušená konfigurácia štádia 
            "Pripomienkové konanie", ktorého začiatok bol nastavený na neskorší dátum a 
            budú obnovené štandardné pracovné kroky ${
                legMaterial?.typ.kod === 'AktEU' ? 'aktuálneho štádia.' : 'štádia "Príprava materiálu".'
              }`}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            className={
              `pracovny-krok__button--danger ${pracovneKroky.pracovneKroky.length > 1
                ? 'pracovny-krok__divider' : ''}`
            }
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.OZNACIT_AKO_SCHVALENY:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete označiť materiál ako schválený?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa spustí štádium '
              + '"Pred vyhlásením v Zbierke zákonov SR" a hodnota parametra "Výsledok rokovania NR SR" v detaile '
              + 'materiálu bude nastavená na "Materiál schválený".'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.OZNACIT_AKO_VZATY_SPAT:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete označiť materiál ako vzatý späť?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa proces vráti do štádia '
              + '"Spracovanie predkladateľom" a hodnota parametra "Výsledok rokovania NR SR" v detaile materiálu bude '
              + 'nastavená na "Materiál vzatý späť".'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.OZNACIT_AKO_NESCHVALENY:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete označiť materiál ako neschválený?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku bude proces ukončený '
              + 'a nebude možné vykonávať ďalšie pracovné kroky. Aktuálne štádium materiálu sa nastaví '
              + 'na "Proces ukončený" a hodnota parametra "Výsledok rokovania NR SR" v detaile materiálu'
              + ' bude nastavená na "Materiál neschválený".'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.PRECHOD_VYHLASENE_V_ZBIERKE:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.vyhlasenievZbierke.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.PRECHOD_ROKOVANIE_NRSR:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion={getMessage(pracovnyKrok.nazov)}
            confirmActionDescription={'Po vykonaní tohto pracovného kroku sa ukončí aktuálne štádium a odstránia sa z '
              + 'Vášho zásobníka práce pracovné kroky, ktoré patria do aktuálneho štádia tohto materiálu a spustí '
              + 'sa nové štádium.'}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ZRUSIT_MATERIAL:
        return (
          <PracovnyKrokComplete
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            confirmActionQuestion="Naozaj si prajete zrušiť proces?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku bude proces zo systému odstránený '
              + 'a nebude ho možné vyhľadať v zozname legislatívnych procesov.'}
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            goToList
            redirect={!ulohy}
            modalClass="confirm-modal"
            className={
              `pracovny-krok__button--danger ${pracovneKroky.pracovneKroky.length > 1
                ? 'pracovny-krok__divider' : ''}`
            }
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.UKONCIT_VYHODNOTENIE_MPK_AKTYEU:
      case PracovnyKrokEnum.UKONCIT_VYHODNOTENIE_MPK:
        return (
          <UkoncitVyhodnotenieMpk
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            aktEu={pracovnyKrok.pracovnyKrok === PracovnyKrokEnum.UKONCIT_VYHODNOTENIE_MPK_AKTYEU}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.UKONCIT_PROCES_POSLEDNE_STADIUM:
      case PracovnyKrokEnum.UKONCIT_PROCES_BEZ_MPK:
      case PracovnyKrokEnum.UKONCIT_PROCES:
        return (
          <UkoncitProces
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ODOSLAT_BEZ_PRIPOMIENOK:
        return (
          <OdoslatBezPripomienok
            legMaterial={legMaterial}
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            pracovnyKrok={pracovnyKrok}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.ODOSLAT_NA_UVSR:
        return (
          <OdoslatNaUvsr
            key={pracovnyKrok.pracovnyKrok}
            legMaterial={legMaterial}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      case PracovnyKrokEnum.VYTVORIT_PRIPOMIENKU:
        return (
          <Button
            key={pracovnyKrok.pracovnyKrok}
            className="pracovny-krok__button"
            buttonStyle="link"
            /* eslint-disable-next-line max-len */
            onClick={() => notifyParent(externalRoutes.pripomienky.add.withParams(legMaterial.cisloLegislativnehoMaterialu))}
            size="small"
          >{pracovnyKrok.nazov}
          </Button>
        );
      case PracovnyKrokEnum.VYHODNOTIT_PRIPOMIENKY:
        return (
          <Button
            key={pracovnyKrok.pracovnyKrok}
            className="pracovny-krok__button"
            buttonStyle="link"
            /* eslint-disable-next-line max-len */
            onClick={() => notifyParent(externalRoutes.pripomienky.list.withParams(legMaterial.cisloLegislativnehoMaterialu))}
            size="small"
          >{pracovnyKrok.nazov}
          </Button>
        );
      case PracovnyKrokEnum.PRECHOD_VYJADRENIA:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={
              routes.procesy.vyjadrenia.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)
            }
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.PRECHOD_MPK:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.mpk.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.SPUSTIT_ROKOVANIE_POV:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.rokovanie.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.ZRUSIT_MPK:
        return (
          <Link
            className={
              `pracovny-krok__button pracovny-krok__button--danger 
              ${pracovneKroky.pracovneKroky.length > 1 ? 'pracovny-krok__divider' : ''}`
            }
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.mpk.cancel.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.DEFINOVAT_STUPEN_DOLEZITOSTI:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.dolezitost.withParams(type, year, index)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.URCIT_GESTOROV:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.gestor.withParams(type, year, index)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.ODMIETNUT_GESTORA:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.gestor.odmietnutie.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.DEFINOVAT_KATEGORIU:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            to={routes.procesy.kategoria.withParams(type, year, index)}
            type="link"
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.NAMIETKA_K_URCENIU_GESTORSTVA:
        return (
          <Link
            className="pracovny-krok__button"
            key={pracovnyKrok.pracovnyKrok}
            size="small"
            type="link"
            to={routes.procesy.namietkaKuGestorstvu.withParams(type, year, index, pracovnyKrok.pracovnyKrokId)}
          >{pracovnyKrok.nazov}
          </Link>
        );
      case PracovnyKrokEnum.ZVEREJNIT_NA_PORTALI:
        return (
          <PracovnyKrokComplete
            key={pracovnyKrok.pracovnyKrok}
            confirmActionQuestion="Naozaj si prajete zverejniť proces na portáli?"
            confirmActionDescription={'Po vykonaní tohto pracovného kroku bude proces viditeľný verejnosti na portáli '
              + 'a zároveň bude ukončený, teda už nebude možné vykonávať pracovné kroky a aktuálne štádium materiálu '
              + 'sa zmení na "Proces ukončený".'}
            pracovnyKrok={pracovnyKrok}
            reloadDetail={reloadDetail}
            reloadPracovneKroky={reloadPracovneKroky}
            legMaterial={legMaterial}
            setIsDropdownOpened={setIsDropdownOpened}
          />
        );
      default:
        return null;
    }
  };

  if (pracovneKroky && pracovneKroky.pracovneKroky.length === 0) {
    return null;
  }

  return pracovneKroky && (
    <div className="material-detail-pracovne-kroky">
      {pracovneKroky.pracovneKroky.map((pracovnyKrok) => getPracovnyKrok(pracovnyKrok))}
    </div>
  );
};
export default PracovneKroky;
